<script lang="ts" setup>
import type { ITableColumnsOptions } from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import {
  GROUPS_OF_ADMINS_PERMISSIONS,
  TRANSLATION_GROUPS_OF_ADMINS_LIST,
} from '@modules/groups-of-admins/constants';
import { useGroupOfAdminsDeleteMutation } from '@modules/groups-of-admins/mutations';
import { useGroupsOfAdminsListQuery } from '@modules/groups-of-admins/queries';
import { GROUPS_OF_ADMINS_CREATE_ROUTE_NAME } from '@modules/groups-of-admins/submodules/create/constants';
import { GROUPS_OF_ADMINS_EDIT_ROUTE_NAME } from '@modules/groups-of-admins/submodules/edit/constants';
import { useI18n } from '@modules/i18n/composables';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const { confirmDelete } = useConfirmDialogs();
const { getTranslation } = useI18n();

const {
  tableState,
  data,
  isLoading,
  isFetching,
  updateTableState,
  refreshTableData,
} = useTableData(useGroupsOfAdminsListQuery);

const columnOptions: ITableColumnsOptions = {
  admin_name: {
    defaultMobilePreview: true,
  },
};

const { mutate: onDelete } = useGroupOfAdminsDeleteMutation();

function removeHandler(event: Event, id: number): void {
  confirmDelete({
    event,
    header: getTranslation(TRANSLATION_GROUPS_OF_ADMINS_LIST, 'TITLE').value,
    message: getTranslation(TRANSLATION_GROUPS_OF_ADMINS_LIST, 'MESSAGE').value,
    acceptLabel: getTranslation(TRANSLATION_GROUPS_OF_ADMINS_LIST, 'YES').value,
    rejectLabel: getTranslation(TRANSLATION_GROUPS_OF_ADMINS_LIST, 'NO').value,
    accept: () => {
      onDelete(id);
    },
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'GroupsOfAdminsListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUPS_OF_ADMINS_LIST, 'HEADER')"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="$hasPermission(GROUPS_OF_ADMINS_PERMISSIONS.EDIT)"
          icon="pi-file-edit"
          :to="{ name: GROUPS_OF_ADMINS_EDIT_ROUTE_NAME, params: { id: listItem.id } }"
        />
        <VIconButton
          v-if="$hasPermission(GROUPS_OF_ADMINS_PERMISSIONS.DELETE)"
          mode="link"
          icon="pi-trash"
          @click="removeHandler($event, listItem.id)"
        />
      </template>
      <template
        #footerButtons
      >
        <VButton
          :label="$getTranslation(TRANSLATION_GROUPS_OF_ADMINS_LIST, 'CREATE')"
          :to="{ name: GROUPS_OF_ADMINS_CREATE_ROUTE_NAME }"
        />
      </template>
    </VTable>
  </VCard>
</template>
